import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle,
  Search,
  X,
  ChevronDown,
  Truck,
  Feather,
  Layers,
  Sparkles,
  ShieldCheck,
  PhoneCall,
  Clock,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  ArrowLeft,
  Package,
  BookOpen,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import { updateSEO } from "../utils/seo";
import "./FAQ.css";

const faqData = [
  // ── Orders & Delivery ──
  {
    id: "delivery-times",
    category: "orders",
    question: {
      ar: "كم تستغرق مدة توصيل الطلبات داخل محافظات مصر؟",
      en: "How long does delivery take across Egyptian governorates?",
    },
    answer: {
      ar: "يتم توصيل الطلبات في محافظتي القاهرة والجيزة خلال 24 إلى 48 ساعة عمل. لباقي محافظات الوجه البحري والإسكندرية تستغرق الشحنة من يومين إلى 3 أيام، بينما تستغرق محافظات الصعيد والبحر الأحمر من 3 إلى 4 أيام عمل كحد أقصى. يمكنك متابعة خط سير شحنتك لحظة بلحظة عبر صفحة طلباتي.",
      en: "Deliveries within Greater Cairo and Giza arrive within 24 to 48 business hours. For Alexandria and Delta governorates, shipments arrive in 2 to 3 days. Upper Egypt and Red Sea destinations take 3 to 4 business days. You can track your package progress in real time under My Orders.",
    },
  },
  {
    id: "inspect-package",
    category: "orders",
    question: {
      ar: "هل يمكنني معاينة المنتجات والتأكد من المقاسات قبل الدفع لمندوب الشحن؟",
      en: "Can I inspect the products and check sizes before paying the courier?",
    },
    answer: {
      ar: "نعم، بكل تأكيد! نحن في تيري لاند نضمن لك حق معاينة وتفقد الشحنة بحضور مندوب شركة الشحن قبل سداد قيمة الطلب لخيارات الدفع عند الاستلام (COD)، لنتأكد من رضاك الكامل عن جودة ونعومة المنسوجات.",
      en: "Yes, absolutely! At Terry Land, we welcome and encourage opening the package in the presence of the courier to inspect the texture, finish, and sizes before paying for Cash on Delivery (COD) orders.",
    },
  },
  {
    id: "change-shipping-details",
    category: "orders",
    question: {
      ar: "هل يمكنني تعديل عنوان التوصيل أو رقم الهاتف بعد تأكيد الطلب؟",
      en: "Can I change my delivery address or phone number after placing an order?",
    },
    answer: {
      ar: "نعم، إذا لم تكن الشحنة قد خرجت بالفعل مع شركة الشحن. كل ما عليك هو التواصل الفوري مع فريق خدمة العملاء عبر الواتساب أو الهاتف، وسنقوم بتحديث العنوان ورقم التواصل فوراً في نظام الشحن.",
      en: "Yes, as long as the package has not yet been dispatched with the courier partner. Simply reach out immediately to our concierge team via WhatsApp or telephone, and we will update your delivery details seamlessly.",
    },
  },

  // ── Fabrics & Quality (Microfiber & Cotton) ──
  {
    id: "fabric-options",
    category: "cotton",
    question: {
      ar: "ما هي خيارات الخامات المتاحة؟ وهل منتجاتكم قطن كامل؟",
      en: "What fabric options are available? Are your products 100% full cotton?",
    },
    answer: {
      ar: `منتجاتنا لا تقتصر على خامة واحدة تقليدية؛ بل نوفر خيارات متطورة تلبي مختلف التفضيلات وتتغلب على عيوب المناشف التقليدية:

• خامة الوجهين المزدوجة (Dual-Face): تجمع أفضل ما في الخامتين — قطن طبيعي ملامس للبشرة يمنح إحساساً طبيعياً وامتصاصاً عالياً، مع مايكروفايبر على الجانب الآخر يمنح النعومة، والخفة، وسرعة الجفاف ومقاومة الانكماش.

• مايكروفايبر فائق النعومة (~320 GSM): ناعم كالحرير، خفيف الوزن، سريع الجفاف للغاية، ومضاد للبكتيريا ومثالي للشعر والرياضة والسفر.

• قطن على الوجهين: ناعم وعالي الامتصاص لمن يفضلون الملمس الكلاسيكي للقطن. يتطلب تجفيفاً ونشراً جيداً بعد الاستخدام لتجنب ظهور روائح الرطوبة والكمكمة.`,
      en: `Our products are not limited to a single traditional fabric; we engineer three distinct materials to meet diverse needs:

• Dual-Face Cotton + Microfiber: Combines the best of both—natural cotton against the skin for authentic absorbency, with plush microfiber on the other side for lightness, cloud-like softness, and rapid drying without musty odors.

• 100% Microfiber (~320 GSM): Ultra-soft, lightweight, antibacterial, and rapid drying—perfect for travel, gym, and friction-free hair drying.

• Double-Sided Cotton: Classic, plush, and substantial absorbency for traditionalists, requiring proper hanging to ensure thorough drying.`,
    },
  },
  {
    id: "bridal-sets-trousseau",
    category: "cotton",
    question: {
      ar: "هل تتوفر أطقم برانص ومفروشات وفوط مخصصة لتجهيز العرائس وجهاز العروسة؟",
      en: "Do you offer dedicated bridal sets, couple bathrobes, and trousseau linens?",
    },
    answer: {
      ar: "نعم، بكل تأكيد! نوفر تشكيلات ملكية متكاملة لجهاز العروسة تشمل أطقم برانص العروسين (كابلز)، أطقم فوط وبشاكير عرايس مطرزة بمقاسات متعددة، وأرواب سبا أنيقة، وتأتي في علب وتغليفات هدايا فاخرة تليق بأفخم حفلات ومناسبات الزفاف.",
      en: "Yes, absolutely! We offer bespoke bridal collections including matching couple bathrobe sets, embroidered multi-piece bridal towel sets, and coordinated luxury spa wraps—all presented in exquisite gift boxes designed for weddings.",
    },
  },
  {
    id: "dualface-features",
    category: "cotton",
    question: {
      ar: "ما هي مميزات خامة الوجهين المزدوجة ولماذا يفضلها الكثيرون؟",
      en: "What are the unique advantages of our Dual-Face fabric?",
    },
    answer: {
      ar: "تعد خامة الوجهين المزدوجة ابتكاراً حصرياً يجمع بين بطانة داخلية من القطن الطبيعي الملامس للبشرة لامتصاص فوري للمياه، وطبقة خارجية مخملية من المايكروفايبر تمنح الروب أو المنشفة نعومة فائقة وسرعة جفاف قياسية، مما يمنع تكون روائح الرطوبة والكمكمة نهائياً ويحافظ على رونق القطعة لسنوات.",
      en: "Our Dual-Face fabric is a breakthrough weave combining an inner skin-contacting layer of absorbent natural cotton with a plush outer microfiber surface. This delivers rapid absorption while drying in a fraction of the time, completely preventing musty odors and ensuring lifelong softness.",
    },
  },
  {
    id: "gsm-explanation",
    category: "cotton",
    question: {
      ar: "ماذا تعني كثافة النسيج GSM وما هي أوزان منتجاتكم؟",
      en: "What does GSM textile density mean, and what are your fabric weights?",
    },
    answer: {
      ar: "يرمز GSM إلى 'جرام لكل متر مربع'؛ وهو المعيار العالمي لكثافة وسماكة النسيج. تأتي خامة المايكروفايبر لدينا بوزن خفيف ورشيق يبلغ حوالي 320 GSM لتوفير نعومة فائقة وسرعة جفاف قياسية، بينما تعتبر خامات القطن بطبيعتها أثقل وزناً لتوفير امتصاص فندقي عميق. وتوفر خامة الوجهين التوازن المثالي بين دلال ملمس القطن وخفة وسرعة جفاف المايكروفايبر.",
      en: "GSM stands for 'Grams per Square Meter'—the universal benchmark for textile density and weight. Our 100% microfiber pieces are crafted at a lightweight ~320 GSM for cloud-like softness and rapid drying, whereas our natural cotton weaves are heavier to deliver substantial hotel-grade absorbency. Our dual-face fabric strikes the ultimate balance between the comforting weight of cotton and the light, quick-drying performance of microfiber.",
    },
  },
  {
    id: "skin-safety",
    category: "cotton",
    question: {
      ar: "هل الصبغات والمواد المستخدمة آمنة على البشرة الحساسة والأطفال؟",
      en: "Are your dyes and materials safe for delicate skin and infants?",
    },
    answer: {
      ar: "نعم، تخضع جميع خيوطنا وصبغاتنا لمعايير الأمان الدولية OEKO-TEX Standard 100، وتخلو تماماً من المبيضات الكلورية والمواد الكيميائية القاسية. خاماتنا آمنة ولطيفة بنسبة 100% على بشرة حديثي الولادة والأشخاص الذين يعانون من الحساسية الجلدية أو الأكزيما.",
      en: "Yes. All yarns and dyes adhere rigorously to OEKO-TEX Standard 100 guidelines, free of harsh chlorine bleaches, phthalates, and formaldehyde. Our textiles are hypoallergenic and 100% safe for newborns and individuals with sensitive skin or eczema.",
    },
  },

  // ── Sizing & Fit ──
  {
    id: "robe-sizing-guide",
    category: "sizing",
    question: {
      ar: "كيف أختار مقاس روب الحمام المناسب لطولي ووزني؟",
      en: "How do I choose the correct bathrobe size for my height and build?",
    },
    answer: {
      ar: `لاختيار مقاس روب الحمام المناسب لطولك ووزنك، يمكنك الاعتماد على القياسات المحددة بدقة لأروابنا ومقارنتها مع تفضيلاتك:

• مقاس L (يناسب الأجسام المتوسطة):
  - الطول من الكتف إلى أسفل الروب: 120 سم
  - العرض من تحت الإبط: 60 سم
  - طول الكم: 55 سم

• مقاس XL (يناسب من يفضلون وسعاً إضافياً):
  - الطول من الكتف إلى أسفل الروب: 120 سم
  - العرض من تحت الإبط: 63 سم
  - طول الكم: 58 سم

• مقاسات الأطفال:
  متوفر مقاسات مناسبة للأعمار: 6 / 8 / 10 / 12 سنة.

نصيحة للمقاس: تتميز جميع الأرواب بقَصّة مريحة مع حزام قابل للتعديل لضبط المقاس على الخصر بسهولة، وإذا كنت تتردد بين مقاسين أو تحب أن يكون الروب فضفاضاً ومريحاً أكثر، ننصح باختيار مقاس XL.`,
      en: `To choose the best bathrobe size for your height and build, you can check our precise measurements below to find the right fit for your preference:

• Size L (Ideal for medium builds):
  - Length from shoulder to bottom: 120 cm
  - Width underarm to underarm: 60 cm
  - Sleeve length: 55 cm

• Size XL (Ideal for taller builds or those who prefer extra room):
  - Length from shoulder to bottom: 120 cm
  - Width underarm to underarm: 63 cm
  - Sleeve length: 58 cm

• Kids' Sizes:
  Available for ages: 6 / 8 / 10 / 12 years.

Fit Recommendation: All our robes are cut with a relaxed unisex drape and come with an adjustable waist belt. If you are between sizes or prefer a more generous, cozy wrap, we recommend selecting size XL.`,
    },
  },
  {
    id: "towel-dimensions",
    category: "sizing",
    question: {
      ar: "ما هي المقاسات والأبعاد الدقيقة لمناشف الوجه واليدين والاستحمام؟",
      en: "What are the exact dimensions of your bath sheets, towels, and hand linens?",
    },
    answer: {
      ar: "تأتي مجموعاتنا بالأبعاد القياسية المعتمدة في أرقى الأجنحة الفندقية:\n• ملاءة حمام عملاقة (Bath Sheet): 100 × 150 سم.\n• منشفة حمام رئيسية (Bath Towel): 70 × 140 سم.\n• منشفة يدين (Hand Towel): 50 × 90 سم.\n• منشفة وجه (Face Towel): 30 × 30 سم.",
      en: "Our collections are tailored to international luxury hotel dimensions:\n• Oversized Bath Sheet: 100 × 150 cm.\n• Standard Bath Towel: 70 × 140 cm.\n• Hand Linen: 50 × 90 cm.\n• Face Cloth: 30 × 30 cm.",
    },
  },

  // ── Care & Laundering ──
  {
    id: "washing-instructions",
    category: "care",
    question: {
      ar: "كيف أغسل المناشف والأرواب للحفاظ على نعومتها وقوة امتصاصها؟",
      en: "How should I wash my towels and robes to preserve softness and absorbency?",
    },
    answer: {
      ar: "يُفضل الغسيل في الغسالة الأوتوماتيكية على دورة غسيل معتدلة بدرجة حرارة 30–40 درجة مئوية مع ألوان مماثلة، باستخدام منظف سائل لطيف. يُنصح بتجنب درجات الحرارة المرتفعة والمنظفات القوية، وعدم استخدام المبيضات إلا إذا كانت مناسبة للخامة.",
      en: "It is best to machine wash on a gentle cycle at 30–40°C with similar colors, using a mild liquid detergent. We recommend avoiding high temperatures and harsh detergents, and refraining from using bleach unless it is specifically suitable for the fabric.",
    },
  },
  {
    id: "shrinkage-and-color",
    category: "care",
    question: {
      ar: "هل تنكمش المناشف بعد الغسيل أو تبهت ألوانها مع مرور الوقت؟",
      en: "Do the towels shrink or fade after multiple washes?",
    },
    answer: {
      ar: "خيوط قطن تيري لاند تمر بعملية غسيل أولي وتمشيط ميكانيكي (Pre-shrunk) تقلل نسبة الانكماش الطبيعي إلى أقل من 3%. كما نستخدم صبغات تفاعلية فائقة الثبات مقاومة للشمس ومياه الغسيل المتكرر.",
      en: "Terry Land yarns undergo mechanical pre-shrinking and double combing, restricting natural shrinkage to under 3%. We apply premium fiber-reactive dyes that bond molecularly with the cotton fibers, ensuring colors remain radiant wash after wash.",
    },
  },

  // ── Returns & Warranty ──
  {
    id: "return-policy",
    category: "returns",
    question: {
      ar: "ما هي سياسة الاستبدال والاسترجاع المتبعة لدى تيري لاند؟",
      en: "What is Terry Land's exchange and return policy?",
    },
    answer: {
      ar: "نحن نلتزم بسياسة إرجاع واستبدال مرنة تمتد إلى 14 يوماً من تاريخ استلام الطلب، شريطة أن تكون المنتجات غير مستخدمة، غير مغسولة، وفي عبوتها الأصلية بكامل بطاقات التعريف. يمكنك طلب الاستبدال بسهولة عبر صفحة اتصل بنا أو الواتساب.",
      en: "We offer a hassle-free 14-day exchange and return policy from delivery, provided pieces are unwashed, unused, and retained in their original packaging with atelier tags intact. Simply contact our concierge via WhatsApp or the Contact page.",
    },
  },
  {
    id: "damaged-item",
    category: "returns",
    question: {
      ar: "ماذا أفعل إذا استلمت منتجاً به عيب مصنعي أو مقاساً غير مطابق؟",
      en: "What should I do if I receive a defective item or incorrect size?",
    },
    answer: {
      ar: "نعتذر بشدة عن أي خطأ نادر كهذا. يتم استبدال أي قطعة بها عيب صناعة أو خطأ في المقاس فوراً وبشكل مجاني بالكامل مع تحملنا لكافة تكاليف الشحن ذهاباً وإياباً. يقوم المندوب بتسليمك القطعة البديلة واستلام القطعة السابقة خلال 48 ساعة.",
      en: "We sincerely apologize for any rare discrepancy. Any item with a manufacturing imperfection or size error will be replaced immediately and completely free of charge. A complimentary courier will deliver the replacement to your doorstep within 48 hours.",
    },
  },
];

export default function FAQ() {
  const { t, isRTL, language } = useLanguage();
  const { addToast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItems, setOpenItems] = useState(new Set(["delivery-times", "fabric-options"]));
  const [helpfulFeedback, setHelpfulFeedback] = useState({});

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map((item) => ({
        "@type": "Question",
        "name": isRTL ? item.question.ar : item.question.en,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": isRTL ? item.answer.ar : item.answer.en,
        },
      })),
    };

    updateSEO({
      title: t("faq.metaTitle"),
      description: isRTL
        ? "إجابات وافية وموثوقة حول خامات المايكروفايبر والقطن، مقاسات البرانص والفوط والبشاكير، تجهيزات العرايس، وطرق العناية والشحن."
        : "Comprehensive answers about our microfiber and cotton fabrics, sizing for bathrobes, towels, bridal sets, and textile care.",
      canonical: "https://terryland.net/faq",
      jsonLd: faqSchema,
    });
  }, [t, isRTL, language]);

  const categories = [
    { id: "all", labelKey: "faq.catAll", icon: HelpCircle },
    { id: "orders", labelKey: "faq.catOrders", icon: Truck },
    { id: "cotton", labelKey: "faq.catCotton", icon: Feather },
    { id: "sizing", labelKey: "faq.catSizing", icon: Layers },
    { id: "care", labelKey: "faq.catCare", icon: Sparkles },
    { id: "returns", labelKey: "faq.catReturns", icon: ShieldCheck },
  ];

  // Filter items by category and search term
  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return faqData.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;
      if (!matchesCategory) return false;

      if (!q) return true;

      const qAr = item.question.ar.toLowerCase();
      const qEn = item.question.en.toLowerCase();
      const aAr = item.answer.ar.toLowerCase();
      const aEn = item.answer.en.toLowerCase();

      return (
        qAr.includes(q) ||
        qEn.includes(q) ||
        aAr.includes(q) ||
        aEn.includes(q)
      );
    });
  }, [searchQuery, activeCategory]);

  const toggleItem = (id) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleHelpful = (id, isHelpful) => {
    if (helpfulFeedback[id]) return;
    setHelpfulFeedback((prev) => ({ ...prev, [id]: isHelpful }));
    addToast(
      isRTL
        ? "شكراً لمشاركتك رأيك القَيّم!"
        : "Thank you for your valuable feedback!",
      "success"
    );
  };

  return (
    <div className="faq-page">
      {/* ── Hero Section ── */}
      <section className="faq-hero">
        <div className="faq-hero__glow" />
        <div className="container faq-hero__inner">
          <div className="faq-badge">
            <HelpCircle size={14} className="faq-badge__icon" />
            <span>{t("faq.eyebrow")}</span>
          </div>

          <h1 className="faq-hero__title">{t("faq.title")}</h1>
          <p className="faq-hero__sub">{t("faq.subtitle")}</p>

          {/* ── Live Search Input ── */}
          <div className="faq-search-wrapper">
            <div className="faq-search-box">
              <Search size={18} className="faq-search-box__icon" />
              <input
                type="text"
                className="faq-search-box__input"
                placeholder={t("faq.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={t("faq.searchAria")}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="faq-search-box__clear"
                  onClick={() => setSearchQuery("")}
                  aria-label={t("faq.clearSearch")}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {searchQuery && (
              <p className="faq-search-count">
                {t("faq.resultsCount").replace("{count}", filteredFaqs.length)}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ── Concierge Shortcuts Bar ── */}
      <section className="faq-concierge-strip">
        <div className="container">
          <div className="faq-concierge-grid">
            <Link to="/account/orders" className="faq-concierge-card">
              <div className="faq-concierge-card__icon-box">
                <Package size={20} />
              </div>
              <div className="faq-concierge-card__text">
                <h3>{t("faq.trackOrder")}</h3>
                <p>{t("faq.trackOrderDesc")}</p>
              </div>
              <div className="faq-concierge-card__arrow">
                {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
              </div>
            </Link>

            <Link to="/care" className="faq-concierge-card">
              <div className="faq-concierge-card__icon-box">
                <BookOpen size={20} />
              </div>
              <div className="faq-concierge-card__text">
                <h3>{t("faq.careGuide")}</h3>
                <p>{t("faq.careGuideDesc")}</p>
              </div>
              <div className="faq-concierge-card__arrow">
                {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
              </div>
            </Link>

            <a
              href={`https://wa.me/${t("contact.whatsappNumber").replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="faq-concierge-card faq-concierge-card--wa"
            >
              <div className="faq-concierge-card__icon-box">
                <PhoneCall size={20} />
              </div>
              <div className="faq-concierge-card__text">
                <h3>{t("faq.waConcierge")}</h3>
                <p>{t("faq.waConciergeDesc")}</p>
              </div>
              <div className="faq-concierge-card__arrow">
                <ExternalLink size={16} />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── Category Tabs & Accordion ── */}
      <section className="faq-content-section">
        <div className="container">
          {/* Categories Bar */}
          <div className="faq-categories-scroll">
            <div className="faq-categories">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = activeCategory === cat.id;
                const count =
                  cat.id === "all"
                    ? faqData.length
                    : faqData.filter((i) => i.category === cat.id).length;

                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`faq-cat-chip ${isActive ? "faq-cat-chip--active" : ""}`}
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    <Icon size={15} />
                    <span>{t(cat.labelKey)}</span>
                    <span className="faq-cat-chip__count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accordion List */}
          <div className="faq-list">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((item) => {
                const isOpen = openItems.has(item.id);
                const questionText = isRTL ? item.question.ar : item.question.en;
                const answerText = isRTL ? item.answer.ar : item.answer.en;
                const catObj = categories.find((c) => c.id === item.category);

                return (
                  <div
                    key={item.id}
                    className={`faq-item ${isOpen ? "faq-item--open" : ""}`}
                  >
                    <button
                      type="button"
                      className="faq-item__header"
                      onClick={() => toggleItem(item.id)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${item.id}`}
                    >
                      <div className="faq-item__title-group">
                        {catObj && (
                          <span className="faq-item__category-badge">
                            {t(catObj.labelKey)}
                          </span>
                        )}
                        <h2 className="faq-item__question">{questionText}</h2>
                      </div>
                      <div className="faq-item__icon-wrap">
                        <ChevronDown size={18} className="faq-item__chevron" />
                      </div>
                    </button>

                    {isOpen && (
                      <div
                        id={`faq-answer-${item.id}`}
                        className="faq-item__body"
                        role="region"
                      >
                        <p className="faq-item__answer">{answerText}</p>

                        {/* Helpful Feedback Box */}
                        <div className="faq-item__footer">
                          <span className="faq-helpful-label">
                            {isRTL
                              ? "هل كانت هذه الإجابة مفيدة؟"
                              : "Was this answer helpful?"}
                          </span>
                          <div className="faq-helpful-actions">
                            <button
                              type="button"
                              className={`faq-helpful-btn ${
                                helpfulFeedback[item.id] === true
                                  ? "faq-helpful-btn--voted"
                                  : ""
                              }`}
                              onClick={() => handleHelpful(item.id, true)}
                              disabled={Boolean(helpfulFeedback[item.id])}
                              aria-label="Helpful"
                            >
                              <ThumbsUp size={14} />
                              <span>{isRTL ? "نعم" : "Yes"}</span>
                            </button>
                            <button
                              type="button"
                              className={`faq-helpful-btn ${
                                helpfulFeedback[item.id] === false
                                  ? "faq-helpful-btn--voted"
                                  : ""
                              }`}
                              onClick={() => handleHelpful(item.id, false)}
                              disabled={Boolean(helpfulFeedback[item.id])}
                              aria-label="Not helpful"
                            >
                              <ThumbsDown size={14} />
                              <span>{isRTL ? "لا" : "No"}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="faq-empty">
                <HelpCircle size={44} className="faq-empty__icon" />
                <h3 className="faq-empty__title">{t("faq.noResultsTitle")}</h3>
                <p className="faq-empty__desc">{t("faq.noResultsDesc")}</p>
                <button
                  type="button"
                  className="btn-outline faq-empty__btn"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("all");
                  }}
                >
                  {t("faq.resetSearch")}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Concierge Help Banner ── */}
      <section className="faq-cta-section">
        <div className="container">
          <div className="faq-cta-banner">
            <div className="faq-cta-banner__content">
              <span className="faq-cta-badge">{t("faq.eyebrow")}</span>
              <h2 className="faq-cta-title">{t("faq.stillHaveQuestions")}</h2>
              <p className="faq-cta-sub">{t("faq.conciergeSub")}</p>
            </div>
            <div className="faq-cta-banner__actions">
              <Link to="/contact" className="btn-primary faq-cta-btn">
                <span>{t("faq.contactCta")}</span>
                {isRTL ? (
                  <ArrowLeft size={16} className="rtl-flip" />
                ) : (
                  <ArrowRight size={16} className="rtl-flip" />
                )}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
